import { HelpCircle, InfoCircle } from '@untitledui/icons';
import {
  type ComponentType,
  createContext,
  type HTMLAttributes,
  type ReactNode,
  type Ref,
  useContext,
} from 'react';
import type {
  InputProps as AriaInputProps,
  TextFieldProps as AriaTextFieldProps,
  TextFieldRenderProps,
} from 'react-aria-components';
import {
  Group as AriaGroup,
  Input as AriaInput,
  TextField as AriaTextField,
} from 'react-aria-components';

import type { ButtonProps } from '@/lib/base/buttons/button';
import { Button } from '@/lib/base/buttons/button';
import { HintText } from '@/lib/base/forms/hint-text';
import { Label } from '@/lib/base/forms/input/label';
import type { SelectSize } from '@/lib/base/forms/select/select-shared';
import { sizes as sharedSizes } from '@/lib/base/forms/select/select-shared';
import { Tooltip, TooltipTrigger } from '@/lib/base/tooltip/tooltip';
import { cx, sortCx } from '@/utils/cx';
import { isReactComponent, isReactNode } from '@/utils/is-react-component';

export interface InputBaseProps extends TextFieldProps {
  /** Tooltip message on hover. */
  tooltip?: string;
  /**
   * Input size. Uses the shared field-control scale (see `SelectSize` in
   * forms/select/select-shared.ts) so an Input, Select, ComboBox, NativeSelect
   * or TextArea at the same `size` render the same height.
   * @default "sm"
   */
  size?: SelectSize;
  /** Placeholder text. */
  placeholder?: string;
  /** Class name for the icon. */
  iconClassName?: string;
  /** Class name for the input. */
  inputClassName?: string;
  /** Class name for the input wrapper. */
  wrapperClassName?: string;
  /** Class name for the tooltip. */
  tooltipClassName?: string;
  /** Keyboard shortcut to display. */
  shortcut?: string | boolean;
  ref?: Ref<HTMLInputElement>;
  groupRef?: Ref<HTMLDivElement>;
  /** Icon component to display on the left side of the input. */
  icon?: ComponentType<HTMLAttributes<HTMLOrSVGElement>> | ReactNode;
  /** Icon component to display on the right side of the input. */
  trailingButtonIcon?: ReactNode;
  /** On click handler for the trailing button. */
  trailingButtonOnClick?: () => void;
  /** Props for the trailing button. */
  trailingButtonProps?: ButtonProps;
  /** Arbitrary node rendered in the trailing slot (e.g. an inline action button).
   * Sits inside the input's box on the right; the input reserves padding for it. */
  trailingAddon?: ReactNode;
  /** Class name for the input group. */
  className?: string;
}

export const InputBase = ({
  ref,
  tooltip,
  shortcut,
  groupRef,
  size = 'sm',
  isInvalid,
  isDisabled,
  icon: Icon,
  placeholder,
  wrapperClassName,
  tooltipClassName,
  inputClassName,
  iconClassName,
  trailingButtonIcon,
  trailingButtonOnClick,
  trailingButtonProps,
  trailingAddon,
  // Omit this prop to avoid invalid HTML attribute warning
  isRequired: _isRequired,
  ...inputProps
}: Omit<InputBaseProps, 'label' | 'hint'>) => {
  // Check if the input has a leading icon or tooltip
  const hasTrailingIcon =
    trailingButtonIcon || tooltip || isInvalid || trailingAddon;
  const hasLeadingIcon = Icon;

  // If the input is inside a `TextFieldContext`, use its context to simplify applying styles
  const context = useContext(TextFieldContext);

  const inputSize = context?.size || size;

  const sizes = sortCx({
    // Padding, font-size and line-height all come from the shared scale — the
    // `size` prop is the single source of truth for a control's geometry. Each
    // step adds only Input-specific affordances (room for the leading/trailing
    // icons and the ⌘K shortcut), never type or vertical padding.
    xs: {
      root: cx(
        sharedSizes.xs.root,
        hasTrailingIcon && 'pr-8',
        hasLeadingIcon && 'pl-8.5'
      ),
      // `size-4` overrides the base `size-5` on the leading icon element (cx is
      // tailwind-merge and the size table is applied last) — a 20px glyph
      // overwhelms the 32px xs field. `iconTrailing` deliberately carries no
      // size: it is also applied to the trailing-button wrapper, where a
      // `size-*` would squash the button, and every trailing glyph already
      // sets `size-4` itself.
      iconLeading: 'left-2.5 size-4',
      iconTrailing: 'right-2.5',
      shortcut: 'pr-2',
    },
    sm: {
      root: cx(
        sharedSizes.sm.root,
        hasTrailingIcon && 'pr-9',
        hasLeadingIcon && 'pl-10'
      ),
      iconLeading: 'left-3',
      iconTrailing: 'right-3',
      shortcut: 'pr-2.5',
    },
    // `md` is responsive: it renders at compact dimensions on mobile and scales
    // up at the `sm` breakpoint, matching the Select sizing system so inputs and
    // selects stay aligned at every viewport. The padding half of that lives in
    // `sharedSizes.md.root`; the icon offsets below track it.
    md: {
      root: cx(
        sharedSizes.md.root,
        hasTrailingIcon && 'pr-9 sm:pr-9.5',
        hasLeadingIcon && 'pl-10 sm:pl-10.5'
      ),
      iconLeading: 'left-3 sm:left-3.5',
      iconTrailing: 'right-3 sm:right-3.5',
      shortcut: 'pr-2.5 sm:pr-3',
    },
    lg: {
      root: cx(
        sharedSizes.lg.root,
        hasTrailingIcon && 'pr-10',
        hasLeadingIcon && 'pl-11'
      ),
      iconLeading: 'left-4',
      iconTrailing: 'right-4',
      shortcut: 'pr-3.5',
    },
    xl: {
      root: cx(
        sharedSizes.xl.root,
        hasTrailingIcon && 'pr-11',
        hasLeadingIcon && 'pl-12'
      ),
      iconLeading: 'left-4',
      iconTrailing: 'right-4',
      shortcut: 'pr-4',
    },
  });

  let leadingIconContent: ReactNode = null;
  if (Icon && isReactComponent(Icon)) {
    const IconComponent = Icon as ComponentType<HTMLAttributes<SVGElement>>;
    leadingIconContent = (
      <IconComponent
        className={cx(
          'pointer-events-none absolute size-5 text-fg-quaternary',
          isDisabled && 'text-fg-disabled',
          sizes[inputSize].iconLeading,
          context?.iconClassName,
          iconClassName
        )}
      />
    );
  } else if (isReactNode(Icon)) {
    leadingIconContent = (
      <div
        className={cx(
          'pointer-events-none absolute size-5 text-fg-quaternary',
          sizes[inputSize].iconLeading,
          context?.iconClassName,
          iconClassName
        )}
      >
        {Icon}
      </div>
    );
  }

  return (
    <AriaGroup
      {...{ isDisabled, isInvalid }}
      ref={groupRef}
      className={({
        isFocusWithin,
        isDisabled,
        isInvalid,
      }: {
        isFocusWithin: boolean;
        isDisabled: boolean;
        isInvalid: boolean;
      }) =>
        cx(
          'relative flex w-full flex-row place-content-center place-items-center rounded-lg bg-primary shadow-xs ring-1 ring-primary transition-shadow duration-100 ease-linear ring-inset',

          isFocusWithin && !isDisabled && 'ring-2 ring-brand',

          // Disabled state styles
          isDisabled && 'cursor-not-allowed bg-disabled_subtle ring-disabled',
          'group-disabled:cursor-not-allowed group-disabled:bg-disabled_subtle group-disabled:ring-disabled',

          // Invalid state styles
          isInvalid && 'ring-error_subtle',
          'group-invalid:ring-error_subtle',

          // Invalid state with focus-within styles
          isInvalid && isFocusWithin && 'ring-2 ring-error',
          isFocusWithin && 'group-invalid:ring-2 group-invalid:ring-error',

          context?.wrapperClassName,
          wrapperClassName
        )
      }
    >
      {leadingIconContent}

      {/* Input field */}
      <AriaInput
        {...(inputProps as AriaInputProps)}
        ref={ref}
        placeholder={placeholder}
        className={cx(
          // No `text-*` here on purpose: font-size and line-height belong to
          // the shared size scale, applied via `sizes[inputSize].root` below.
          // A base `text-*` is what forced the old padding-only local table and
          // let ancestor `[&_input]:text-*` overrides silently win.
          'm-0 w-full bg-transparent text-primary ring-0 outline-hidden placeholder:text-placeholder autofill:rounded-lg autofill:text-primary',
          isDisabled && 'cursor-not-allowed text-disabled',
          sizes[inputSize].root,
          context?.inputClassName,
          inputClassName
        )}
      />

      {trailingAddon && (
        <div className='absolute inset-y-0 right-1.5 flex items-center'>
          {trailingAddon}
        </div>
      )}

      {trailingButtonIcon ? (
        <div
          className={cx(
            sizes[inputSize].iconTrailing,
            context?.tooltipClassName,
            tooltipClassName,
            'h-10/12 mr-2'
          )}
        >
          <Button
            color='secondary'
            onClick={trailingButtonOnClick}
            {...trailingButtonProps}
          >
            {trailingButtonIcon}
          </Button>
        </div>
      ) : null}

      {/* Tooltip and help icon */}
      {tooltip && !isInvalid && (
        <Tooltip title={tooltip} placement='top'>
          <TooltipTrigger
            className={cx(
              'absolute cursor-pointer text-fg-quaternary transition duration-200 hover:text-fg-quaternary_hover focus:text-fg-quaternary_hover',
              sizes[inputSize].iconTrailing,
              context?.tooltipClassName,
              tooltipClassName
            )}
          >
            <HelpCircle className='size-4' />
          </TooltipTrigger>
        </Tooltip>
      )}

      {/* Invalid icon */}
      {isInvalid && (
        <InfoCircle
          className={cx(
            'pointer-events-none absolute size-4 text-fg-error-secondary',
            sizes[inputSize].iconTrailing,
            context?.tooltipClassName,
            tooltipClassName
          )}
        />
      )}

      {/* Shortcut */}
      {shortcut && (
        <div
          className={cx(
            'pointer-events-none absolute inset-y-0.5 right-0.5 z-10 flex items-center rounded-r-[inherit] bg-linear-to-r from-transparent to-bg-primary to-40% pl-8',
            sizes[inputSize].shortcut
          )}
        >
          <span
            className={cx(
              'pointer-events-none rounded px-1 py-px text-xs font-medium text-quaternary ring-1 ring-secondary select-none ring-inset',
              isDisabled && 'bg-transparent text-disabled'
            )}
            aria-hidden='true'
          >
            {typeof shortcut === 'string' ? shortcut : '⌘K'}
          </span>
        </div>
      )}
    </AriaGroup>
  );
};

InputBase.displayName = 'InputBase';

interface BaseProps {
  /** Label text or element for the input */
  label?: string | ReactNode;
  /** Helper text displayed below the input */
  hint?: ReactNode;
}

interface TextFieldProps
  extends
    BaseProps,
    AriaTextFieldProps,
    Pick<
      InputBaseProps,
      | 'size'
      | 'wrapperClassName'
      | 'inputClassName'
      | 'iconClassName'
      | 'tooltipClassName'
    > {
  ref?: Ref<HTMLDivElement>;
}

const TextFieldContext = createContext<TextFieldProps>({});

export const TextField = ({ className, ...props }: TextFieldProps) => {
  return (
    <TextFieldContext.Provider value={props}>
      <AriaTextField
        {...props}
        data-input-wrapper
        className={(state: TextFieldRenderProps) =>
          cx(
            'group flex h-max w-full flex-col items-start justify-start gap-1.5',
            typeof className === 'function'
              ? className(state as never)
              : className
          )
        }
      />
    </TextFieldContext.Provider>
  );
};

TextField.displayName = 'TextField';

interface InputProps extends InputBaseProps, BaseProps {
  /** Whether to hide required indicator from label */
  hideRequiredIndicator?: boolean;
  /** Controls whether error styling is shown. When false, suppresses error UI even if isInvalid is true. */
  showError?: boolean;
}

export const Input = ({
  size = 'sm',
  placeholder,
  icon: Icon,
  label,
  hint,
  shortcut,
  hideRequiredIndicator,
  showError,
  className,
  ref,
  groupRef,
  tooltip,
  iconClassName,
  inputClassName,
  wrapperClassName,
  tooltipClassName,
  trailingButtonIcon,
  trailingButtonOnClick,
  trailingButtonProps,
  trailingAddon,
  ...textFieldProps
}: InputProps) => {
  const effectiveIsInvalid = (showError ?? true) && !!textFieldProps.isInvalid;

  return (
    <TextField
      // Placeholder-as-accessible-name is a LAST-RESORT fallback for an input
      // with no label at all. It must not fire when the field is labelled
      // elsewhere: an explicit `aria-label` OVERRIDES an associated
      // `<label for>`, so a FormField-wrapped Input would announce its
      // placeholder ("Ada") instead of its label ("First name"). Suppress it
      // whenever the caller supplies `aria-labelledby` (which FormField now
      // provides as `labelId`).
      aria-label={
        !label && !textFieldProps['aria-labelledby'] ? placeholder : undefined
      }
      {...textFieldProps}
      isInvalid={effectiveIsInvalid}
      className={className}
    >
      {({
        isRequired,
        isInvalid,
      }: {
        isRequired: boolean;
        isInvalid: boolean;
      }) => (
        <>
          {label && (
            <Label
              isRequired={
                hideRequiredIndicator ? !hideRequiredIndicator : isRequired
              }
            >
              {label}
            </Label>
          )}

          <InputBase
            ref={ref}
            groupRef={groupRef}
            size={size}
            placeholder={placeholder}
            icon={Icon}
            shortcut={shortcut}
            iconClassName={iconClassName}
            inputClassName={inputClassName}
            wrapperClassName={wrapperClassName}
            tooltipClassName={tooltipClassName}
            tooltip={tooltip}
            trailingButtonIcon={trailingButtonIcon}
            trailingButtonOnClick={trailingButtonOnClick}
            trailingButtonProps={trailingButtonProps}
            trailingAddon={trailingAddon}
            isInvalid={isInvalid}
          />

          {hint && (
            <HintText isInvalid={isInvalid} showError={showError}>
              {hint}
            </HintText>
          )}
        </>
      )}
    </TextField>
  );
};

Input.displayName = 'Input';
